import { useEffect, useMemo, useState } from 'react';

import {
  distributionMapSelector,
  filterColorSelector,
  filterSelector,
  intervalSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import { useSelector } from 'react-redux';
import { lineChartReqBody } from '../utils/metricCardsReqBodyTemplate';
import moment from 'moment';
import { getDistributionData } from '@src/api/metricContainer';
import { useQuery } from 'react-query';
import { get, isEmpty } from 'lodash';
import { getDistributionOption } from '../utils/distributionOption';
import useSetDistributionExport from './export/useSetDistributionExport';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';

function useFetchDistribution({ distributionConfig, enabled = false }) {
  const filter = useSelector(filterSelector);
  const interval = useSelector(chartIntervalSelector);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const { metric = null, dimension } =  distributionConfig

  // build query
  const query = useMemo(() => {
    const body = JSON.parse(JSON.stringify(lineChartReqBody));
    const q = body.queries[0];

    q.filter = filter;
    q.metrics = [metric];
    q.dimension = dimension;
    q.interval = `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`;
    q.isLiveMode = realTimeOnTrends;
    q.queryGranularity = "ALL";

    delete q.granularity;
    delete q.type;
    delete q.options;

    return q;
  }, [filter, interval, metric, dimension, realTimeOnTrends]);

  const { isFetching, data, isError, refetch } = useQuery(
    ['distribution', query],
    () => {
      return getDistributionData(query).then((response) => {

        const rawData = get(response, ['data', 'data', '0', 'result'], []);
        const formattedData = rawData.map(row => {
          return [
            row[dimension],
            row[metric],
          ];
        });

        return formattedData;
      })
    }, {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled && !!metric,
  });

  return { isFetching, data, isError, refetch, interval };
}


function useDistribution({ instance, metric: chartMetric, enabled, additionalTriggerDeps = [] }) {
  const distributionMetrics = useSelector(distributionMapSelector);
  const filterColor = useSelector(filterColorSelector);
  const [distributionIdx, setDistributionIdx] = useState(0);

  // distribution params
  const distributionConfig = get(distributionMetrics, [chartMetric, distributionIdx], {});

  const { isFetching, data, isError, refetch, interval } = useFetchDistribution({
    distributionConfig,
    enabled
  });

  // distribution replaces whole chart option
  const distributionChartData = useMemo(() => {
    return !isEmpty(distributionConfig) ? getDistributionOption({
      data,
      chartMetric,
      filterColor,
      distributionConfig,
      interval,
    }): null;
  }, [data, chartMetric, filterColor, distributionConfig]);


  // for retry wrapper
  const distributionQueryState = {
    isLoading: isFetching,
    showError: isError,
    fetchData: refetch,
  }

  useSetDistributionExport({ metric: chartMetric, data });

  useEffect(() => {
    if (instance && enabled) {
      const option = instance.getOption();
      if (option) {
        // clean merge if moving from timeseries to distribution view
        instance.setOption(distributionChartData, { notMerge: option._viewType === 'timeseries'  });
      }
    }
  }, [instance, enabled, distributionChartData, ...additionalTriggerDeps]);

  return {
    distributionChartData,
    distributionQueryState,
    distribution: distributionIdx,
    selectDistribution: setDistributionIdx,
  };

}

export default useDistribution;
